import { useEffect, useMemo, useRef, useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import {
  IconeCantoneira,
  IconeClipe,
  Aviso,
  Botao,
  CampoDeSelecao,
  EstadoVazio,
  MenuDeAcoes,
  Modal,
  Selo,
  Tabela,
  type ColunaTabela,
  type TomSemantico,
  useDialogo,
} from '../../componentes/ui';
import {
  useAnexarNoItem,
  useCancelar,
  useEnviarAgora,
  useRemoverLote,
  useReprogramar,
  type EtapaLiberada,
  type ResultadoDoAnexo,
} from '../../hooks/useCronogramaAcoes';
import { ModalDeAprovacao } from './AprovacaoDoItem';
import { ModalDeInsercaoEmLote } from './InsercaoEmLote';
import type { DocumentoDaLinha, LinhaCronograma } from '../../servicos/operacao';
import { formatarCompetencia } from '../../servicos/formato';
import { diaLocal, type ColunaDaPlanilha } from '../../servicos/planilha';
import { BotaoExtrairXlsx } from '../../componentes/BotaoExtrairXlsx';
import { ErroDaApi } from '../ErroDaApi';

/**
 * Cronograma de envios, com as três ações do Deca 1.
 *
 * | Ação          | O que faz                                                |
 * | ------------- | -------------------------------------------------------- |
 * | Enviar agora  | Dispara fora da data, marcando `ENVIADO_MANUAL`          |
 * | Reprogramar   | Move a data e devolve a linha para `AGENDADO`            |
 * | Cancelar      | Encerra a linha sem enviar, guardando o motivo           |
 *
 * Cancelar **não apaga**. A linha registra que o e-mail foi planejado, e sumir
 * com ela levaria junto o motivo de ele não ter saído — quem abrir o cronograma
 * daqui a seis meses precisa distinguir "nunca foi agendado" de "foi agendado e
 * alguém cancelou".
 *
 * ## O filtro por mês
 *
 * O Deca 1 filtra por mês do agendamento, e faz sentido: a operação trabalha por
 * competência, e a pergunta é sempre "o que sai em fevereiro". O filtro aqui
 * também considera o **ano**, porque a fila atravessa exercícios — sem isso,
 * "Janeiro" misturaria 2025 e 2026 na mesma lista.
 */
/** Um envio por linha: quando, qual passo, para quem e em que pé está. */
const COLUNAS_DA_PLANILHA: readonly ColunaDaPlanilha<LinhaCronograma>[] = [
  {
    titulo: 'Mês de operação',
    largura: 14,
    valor: (l) => (l.mesOperacao === null ? null : formatarCompetencia(l.mesOperacao)),
  },
  // A tela mostra a data agendada em UTC (é um dia, não um instante).
  { titulo: 'Data de envio', formato: 'data', valor: (l) => l.dataAgendamento.slice(0, 10) },
  {
    titulo: 'Passo',
    largura: 26,
    valor: (l) =>
      l.passo === null
        ? 'Envio avulso'
        : `${l.passo.ordem > 0 ? `${l.passo.ordem}. ` : ''}${l.passo.rotuloCurto}`,
  },
  { titulo: 'Para quem', largura: 16, valor: (l) => l.passo?.paraQuemRotulo ?? null },
  { titulo: 'E-mail', largura: 50, valor: (l) => l.titulo },
  {
    titulo: 'SPED',
    largura: 12,
    valor: (l) => (l.competencia === null ? null : formatarCompetencia(l.competencia)),
  },
  { titulo: 'Situação', largura: 22, valor: (l) => rotuloDoStatus(l.status) },
  {
    titulo: 'Próxima ação',
    largura: 44,
    valor: (l) =>
      `${l.proximaAcao.responsavelRotulo === null ? '' : `${l.proximaAcao.responsavelRotulo}: `}${l.proximaAcao.resumo}`,
  },
  { titulo: 'Enviado em', formato: 'data', valor: (l) => diaLocal(l.dataEnvio) },
  { titulo: 'Anexos', largura: 8, valor: (l) => l.anexos },
];

export function CronogramaDeEnvios(props: {
  readonly clienteId: string;
  readonly linhas: readonly LinhaCronograma[];
  readonly carregando: boolean;
  /** Ano da operação escolhido na aba. Vira o filtro inicial. */
  readonly ano?: number;
  /**
   * A fila é da operação: quem não é admin (o assessor, o fornecedor) vê o que
   * vai sair e quando, sem enviar, reprogramar, anexar ou tirar da fila.
   */
  readonly somenteLeitura?: boolean;
}): JSX.Element {
  const leitura = props.somenteLeitura === true;
  const [mes, setMes] = useState('');
  // O ano herda o da aba: quem trocou para 2026 lá em cima não espera ver a
  // fila de 2025 aqui embaixo. Continua trocável.
  const [ano, setAno] = useState(props.ano === undefined ? '' : String(props.ano));

  useEffect(() => {
    if (props.ano !== undefined) setAno(String(props.ano));
  }, [props.ano]);
  const [reprogramando, setReprogramando] = useState<LinhaCronograma | null>(null);
  const [inserindo, setInserindo] = useState(false);
  const [vendoAprovacao, setVendoAprovacao] = useState<LinhaCronograma | null>(null);
  const [selecionados, setSelecionados] = useState<ReadonlySet<string>>(new Set());
  // Os meses recolhidos: o mês fechado mostra só o cabeçalho e o que espera.
  const [recolhidos, setRecolhidos] = useState<ReadonlySet<string>>(new Set());
  const [avisoDeAnexo, setAvisoDeAnexo] = useState<string | null>(null);

  const anexar = useAnexarNoItem(props.clienteId);

  const enviar = useEnviarAgora(props.clienteId);
  const cancelar = useCancelar(props.clienteId);
  const removerLote = useRemoverLote(props.clienteId);
  const { confirmar, pedirTexto } = useDialogo();

  const anos = useMemo(
    () => [...new Set(props.linhas.map((l) => anoDeOperacao(l)))].sort((a, b) => b - a),
    [props.linhas],
  );

  const filtradas = useMemo(
    () =>
      props.linhas.filter((l) => {
        // O filtro é pelo mês de OPERAÇÃO (o grupo da tela); o que não é de
        // um mês (descritivo, e-mails do contrato) entra pelo ano e ignora o mês.
        if (ano !== '' && String(anoDeOperacao(l)) !== ano) return false;
        if (mes !== '' && l.mesOperacao !== null && String(Number(l.mesOperacao.slice(5))) !== mes)
          return false;
        return true;
      }),
    [props.linhas, mes, ano],
  );

  /**
   * Agrupado por mês de operação (lista de 21/09): cada mês tem o seu fluxo —
   * pedido ao fornecedor, certificado, ajuste do SPED, pagamento — e a leitura
   * é "como está fevereiro", não "o que sai dia 12". O que não pertence a um
   * mês (descritivo, e-mails do contrato, envio avulso) fica no primeiro grupo.
   */
  const grupos = useMemo(() => {
    const porMes = new Map<string, LinhaCronograma[]>();
    for (const l of filtradas) {
      const chave = l.mesOperacao ?? '';
      const lista = porMes.get(chave) ?? [];
      lista.push(l);
      porMes.set(chave, lista);
    }
    return [...porMes.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([chave, itens]) => ({
        chave,
        titulo: chave === '' ? 'Contrato e exercício' : rotuloDoMesDeOperacao(chave),
        // Dentro do mês, a ordem é a do fluxo, não a da data: o certificado ao
        // cliente sai na hora em que o fornecedor responde, e pela data ele
        // aparecia antes do pedido que o originou. O que não é etapa do mês
        // (descritivo, avulso) fica no fim.
        itens: [...itens].sort(
          (a, b) =>
            ordemNoFluxo(a) - ordemNoFluxo(b) ||
            new Date(a.dataAgendamento).getTime() - new Date(b.dataAgendamento).getTime(),
        ),
      }));
  }, [filtradas]);

  const vencidos = filtradas.filter(
    (l) => l.status === 'AGENDADO' && new Date(l.dataAgendamento) < new Date(),
  ).length;

  /**
   * Só o que ainda não saiu entra na seleção.
   *
   * O checkbox existe para a remoção em lote, e e-mail enviado não se remove
   * (é o registro de que o contribuinte foi avisado). Oferecer a caixa e depois
   * recusar a ação seria pior do que não oferecer.
   */
  const selecionaveis = filtradas.filter((l) => l.status === 'AGENDADO' || l.status === 'FALHOU');
  const marcadosVisiveis = selecionaveis.filter((l) => selecionados.has(l.id));
  const todosMarcados =
    selecionaveis.length > 0 && marcadosVisiveis.length === selecionaveis.length;

  /**
   * Cancelar pede o motivo antes de agir. O diálogo devolve `null` quando a
   * pessoa desiste e `''` quando confirma sem escrever nada — os dois casos
   * são diferentes: o primeiro não faz nada, o segundo cancela sem motivo.
   */
  const pedirCancelamento = async (l: LinhaCronograma): Promise<void> => {
    const motivo = await pedirTexto({
      titulo: `Cancelar "${l.titulo}"?`,
      descricao:
        'A linha não é apagada: fica registrada como cancelada, com o motivo, para quem abrir o cronograma depois saber por que o e-mail não saiu.',
      rotulo: 'Motivo (opcional)',
      multilinha: true,
      rotuloConfirmar: 'Cancelar envio',
    });
    if (motivo === null) return;
    cancelar.mutate(motivo === '' ? { emailId: l.id } : { emailId: l.id, motivo });
  };

  const pedirRemocaoEmLote = async (): Promise<void> => {
    const ids = marcadosVisiveis.map((l) => l.id);
    const ok = await confirmar({
      titulo: `Remover ${ids.length} agendamento(s) da fila?`,
      descricao:
        'Apagados de vez — diferente de Cancelar, que mantém o registro com o motivo. Use Cancelar quando quiser saber depois por que o e-mail não saiu.',
      rotuloConfirmar: 'Remover',
      tom: 'perigo',
    });
    if (ok) removerLote.mutate(ids, { onSuccess: () => setSelecionados(new Set()) });
  };

  const alternarLinha = (id: string): void => {
    setSelecionados((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  };

  // "Marcar tudo" age sobre o que está **filtrado**, não sobre a fila inteira:
  // quem filtrou março e clicou no cabeçalho quer março, não o ano.
  const alternarTodos = (): void => {
    setSelecionados((atual) => {
      const proximo = new Set(atual);
      for (const l of selecionaveis) {
        if (todosMarcados) proximo.delete(l.id);
        else proximo.add(l.id);
      }
      return proximo;
    });
  };

  const alternarMes = (chave: string): void => {
    setRecolhidos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(chave)) proximo.delete(chave);
      else proximo.add(chave);
      return proximo;
    });
  };

  const todosRecolhidos = grupos.length > 0 && grupos.every((g) => recolhidos.has(g.chave));

  /**
   * O que o anexo destravou, dito em voz alta.
   *
   * Sem isto o operador anexa o certificado e não tem sinal de que o fluxo
   * andou: a etapa seguinte sai de "Aguarda etapa anterior" no meio da lista,
   * e quem anexou não estava olhando para lá.
   */
  const anunciarAnexo = (resultado: ResultadoDoAnexo): void => {
    const etapas = resultado.etapasLiberadas ?? [];
    // D-008 — o certificado de apropriação é o que faz o mês contar.
    const aproveitado =
      resultado.mesAproveitado === true ? ' O mês passou a contar como aproveitado.' : '';

    if (etapas.length === 0) {
      setAvisoDeAnexo(`Arquivo anexado ao e-mail.${aproveitado}`);
      return;
    }
    const nomes = etapas.map((e) => `"${e.rotulo}"`).join(' e ');
    const uma = etapas.length === 1;
    setAvisoDeAnexo(
      `Arquivo anexado. ${uma ? 'A etapa' : 'As etapas'} ${nomes} ${uma ? 'saiu' : 'saíram'} da ` +
        `espera e ${uma ? 'está agendada' : 'estão agendadas'} para envio.${aproveitado}`,
    );
  };

  const colunas: readonly ColunaTabela<LinhaCronograma>[] = [
    {
      chave: 'selecao',
      titulo: (
        <input
          type="checkbox"
          checked={todosMarcados}
          onChange={alternarTodos}
          disabled={selecionaveis.length === 0}
          aria-label="Marcar todos os itens do filtro atual"
          className="caixa-de-marcar"
        />
      ),
      larguraMinima: '2.5rem',
      celula: (l) => {
        const removivel = l.status === 'AGENDADO' || l.status === 'FALHOU';
        if (!removivel) return <span className="sr-only">Item enviado, não removível</span>;
        return (
          <input
            type="checkbox"
            checked={selecionados.has(l.id)}
            onChange={() => alternarLinha(l.id)}
            aria-label={`Selecionar ${l.titulo}`}
            className="caixa-de-marcar"
          />
        );
      },
    },
    {
      chave: 'dataAgendamento',
      titulo: 'Data de envio',
      larguraMinima: '8rem',
      celula: (l) => (
        <span className="valor-fiscal whitespace-nowrap text-sm">
          {new Date(l.dataAgendamento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
        </span>
      ),
    },
    {
      chave: 'emailId',
      titulo: 'E-mail',
      larguraMinima: '22rem',
      // O título é o assunto cadastrado no Postmark (lista de 17/09), não o
      // nome do tipo no sistema: é o que o destinatário vê na caixa de entrada.
      // Em cima dele, o passo do fluxo; embaixo, os documentos que a etapa
      // envolve — é o que responde "o que já subiu e o que falta".
      celula: (l) => (
        <span className="flex flex-col gap-1">
          {l.passo !== null && (
            <span className="text-xs uppercase tracking-wide text-conteudo-sutil">
              {l.passo.ordem > 0 && `${l.passo.ordem}. `}
              {l.passo.rotuloCurto} · para {l.passo.paraQuemRotulo.toLowerCase()}
            </span>
          )}
          <span className="flex items-center gap-2 text-sm text-conteudo-primario">
            {l.titulo}
            {l.anexos > 0 && (
              <span
                className="inline-flex items-center gap-0.5 rounded-full border border-borda px-1.5 py-0.5 text-xs text-conteudo-secundario"
                title={`${l.anexos} arquivo(s) anexado(s)`}
                aria-label={`${l.anexos} arquivo(s) anexado(s)`}
              >
                <IconeClipe className="h-3.5 w-3.5" />
                {l.anexos}
              </span>
            )}
          </span>
          {l.competencia !== null && (
            <span className="text-xs text-conteudo-sutil">
              SPED {formatarCompetencia(l.competencia)}
            </span>
          )}
          {l.documentos.some((d) => mostrarDocumento(d, l.status)) && (
            <span className="flex flex-wrap gap-1">
              {l.documentos
                .filter((d) => mostrarDocumento(d, l.status))
                .map((d) => (
                  <ChipDeDocumento key={`${d.chave}-${d.papel}`} documento={d} />
                ))}
            </span>
          )}
        </span>
      ),
    },
    {
      chave: 'status',
      titulo: 'Situação e próxima ação',
      larguraMinima: '15rem',
      celula: (l) => (
        <span className="flex flex-col gap-1">
          <Selo tom={tomDoStatus(l.status)}>{rotuloDoStatus(l.status)}</Selo>
          <span className={`text-xs ${COR_DA_ACAO[l.proximaAcao.tom]}`}>
            {l.proximaAcao.responsavelRotulo !== null && (
              <strong className="font-semibold">{l.proximaAcao.responsavelRotulo}: </strong>
            )}
            {l.proximaAcao.resumo}
          </span>
        </span>
      ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '22rem',
      celula: (l) => {
        // "Detalhes": o que o decisor respondeu e os arquivos anexados — vale
        // para qualquer item, enviado ou não.
        const detalhes = (
          <Botao variante="sutil" tamanho="pequeno" onClick={() => setVendoAprovacao(l)}>
            Detalhes
          </Botao>
        );
        // Anexar sem abrir o detalhe: o documento chega por WhatsApp, e o
        // caminho antigo eram três cliques até o mesmo seletor de arquivo.
        const anexo = <AnexoRapido linha={l} anexar={anexar} aoAnexar={anunciarAnexo} />;
        const enviado =
          l.status === 'ENVIADO' || l.status === 'ENVIADO_MANUAL' || l.status === 'CONCLUIDO';

        // E-mail que saiu fora da fila (transição do contrato, envio avulso):
        // fica registrado, mas não tem montagem para reenviar daqui.
        if (!l.reenviavel) {
          return (
            <span className="flex items-center justify-end gap-1">
              {anexo}
              {detalhes}
            </span>
          );
        }

        // RN-622 — etapa que aguarda a anterior: não se envia (o e-mail leva
        // o certificado que ainda não chegou). Só a previsão pode mudar.
        if (l.status === 'PENDENTE') {
          return (
            <span className="flex flex-wrap items-center justify-end gap-2">
              <span className="text-xs text-conteudo-sutil">
                Libera quando a etapa anterior for respondida
              </span>
              {anexo}
              <Botao variante="sutil" tamanho="pequeno" onClick={() => setReprogramando(l)}>
                Reprogramar previsão
              </Botao>
            </span>
          );
        }

        // Enviado também pode sair de novo ou ser reprogramado (lista de 21/09):
        // o destinatário perdeu, o contato mudou, o anexo foi trocado.
        return (
          <span className="flex flex-wrap items-center justify-end gap-2">
            {anexo}
            {detalhes}
            <Botao
              variante="secundario"
              tamanho="pequeno"
              carregando={enviar.isPending && enviar.variables === l.id}
              onClick={() => enviar.mutate(l.id)}
            >
              {enviado ? 'Enviar de novo' : 'Enviar agora'}
            </Botao>
            <Botao variante="sutil" tamanho="pequeno" onClick={() => setReprogramando(l)}>
              Reprogramar
            </Botao>
            {!enviado && (
              <Botao
                variante="sutil"
                tamanho="pequeno"
                carregando={cancelar.isPending && cancelar.variables?.emailId === l.id}
                onClick={() => void pedirCancelamento(l)}
              >
                Cancelar
              </Botao>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight">
            Cronograma de envios
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-conteudo-secundario">
            A fila deste cliente. Um worker varre os agendamentos todo dia e envia o que está na
            data — não depende de alguém lembrar de clicar.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {/* A fila inteira do cliente, não o mês filtrado — com o mês numa coluna. */}
          <BotaoExtrairXlsx
            titulo="Cronograma de envios"
            disabled={props.carregando}
            colunas={COLUNAS_DA_PLANILHA}
            linhas={props.linhas}
          />
          {grupos.length > 1 && (
            <Botao
              variante="sutil"
              onClick={() =>
                setRecolhidos(todosRecolhidos ? new Set() : new Set(grupos.map((g) => g.chave)))
              }
            >
              {todosRecolhidos ? 'Expandir todos' : 'Recolher todos'}
            </Botao>
          )}
          {props.ano !== undefined && !leitura && (
            <Botao onClick={() => setInserindo(true)}>Inserir e-mails</Botao>
          )}
          <CampoDeSelecao
            rotulo="Mês"
            className="w-40"
            value={mes}
            onChange={(e) => setMes(e.target.value)}
            opcoes={[
              { valor: '', rotulo: 'Todos os meses' },
              ...MESES.map((m, i) => ({ valor: String(i + 1), rotulo: m })),
            ]}
          />
          {anos.length > 0 && (
            <CampoDeSelecao
              rotulo="Ano"
              className="w-28"
              value={ano}
              onChange={(e) => setAno(e.target.value)}
              opcoes={[
                { valor: '', rotulo: 'Todos os anos' },
                ...anos.map((a) => ({ valor: String(a), rotulo: String(a) })),
              ]}
            />
          )}
        </div>
      </div>

      {vencidos > 0 && (
        <Aviso tom="atencao" titulo={`${vencidos} agendamento(s) vencido(s)`}>
          Passaram da data e continuam na fila. O worker só envia o que está dentro da janela de
          atraso configurada — o que passou dela vira falha, em vez de disparar de uma vez.
        </Aviso>
      )}

      {marcadosVisiveis.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-acento-ciano/40 bg-acento-ciano/5 px-4 py-3">
          <span className="text-sm text-conteudo-primario">
            {marcadosVisiveis.length} item(ns) selecionado(s)
          </span>
          <span className="flex flex-wrap gap-2">
            <Botao variante="sutil" tamanho="pequeno" onClick={() => setSelecionados(new Set())}>
              Limpar seleção
            </Botao>
            <Botao
              variante="perigo"
              tamanho="pequeno"
              carregando={removerLote.isPending}
              onClick={() => void pedirRemocaoEmLote()}
            >
              Remover da fila
            </Botao>
          </span>
        </div>
      )}

      {avisoDeAnexo !== null && (
        <Aviso tom="positivo" titulo="Anexo guardado">
          <span className="flex flex-wrap items-center gap-3">
            {avisoDeAnexo}
            <button
              type="button"
              onClick={() => setAvisoDeAnexo(null)}
              className="text-xs underline underline-offset-4"
            >
              Fechar
            </button>
          </span>
        </Aviso>
      )}

      {anexar.isError && <ErroDaApi titulo="Não foi possível anexar" erro={anexar.error} />}

      {removerLote.isError && (
        <ErroDaApi titulo="Não foi possível remover" erro={removerLote.error} />
      )}
      {enviar.isError && <ErroDaApi titulo="Não foi possível enviar" erro={enviar.error} />}
      {cancelar.isError && <ErroDaApi titulo="Não foi possível cancelar" erro={cancelar.error} />}

      {enviar.data?.ensaio === true && (
        <Aviso tom="informacao" titulo="Modo ensaio — o e-mail não saiu">
          A mensagem foi montada e os destinatários resolvidos ({enviar.data.destinatarios.length}),
          mas o envio está contido. A linha continua agendada. Defina{' '}
          <span className="valor-fiscal text-xs">EMAIL_ENVIO_REAL=1</span> para liberar.
        </Aviso>
      )}

      {!props.carregando && filtradas.length === 0 ? (
        <EstadoVazio
          titulo={props.linhas.length === 0 ? 'Nada agendado' : 'Nada neste período'}
          descricao={
            props.linhas.length === 0
              ? 'A fila deste cliente está vazia. Os agendamentos nascem do fechamento do contrato.'
              : 'Nenhum envio no mês selecionado.'
          }
          {...(props.linhas.length > 0
            ? {
                acao: (
                  <Botao
                    variante="secundario"
                    onClick={() => {
                      setMes('');
                      setAno('');
                    }}
                  >
                    Limpar filtro
                  </Botao>
                ),
              }
            : {})}
        />
      ) : (
        <div className="flex flex-col gap-6">
          {grupos.map((g) => {
            const aberto = !recolhidos.has(g.chave);
            const enviados = g.itens.filter((i) =>
              ['ENVIADO', 'ENVIADO_MANUAL', 'CONCLUIDO'].includes(i.status),
            ).length;
            const travado = g.itens.find((i) => i.proximaAcao.tom !== 'concluido');

            return (
              <section key={g.chave} aria-label={g.titulo} className="flex flex-col gap-2">
                {/*
                  O mês é um painel: recolhido, mostra só o nome, quanto já saiu
                  e o que ele espera. Doze meses abertos de uma vez viravam uma
                  rolagem de tela e meia para achar o mês de quem pergunta.
                */}
                <button
                  type="button"
                  onClick={() => alternarMes(g.chave)}
                  aria-expanded={aberto}
                  className="flex w-full flex-wrap items-center justify-between gap-3 rounded-card border border-borda bg-superficie-elevada px-4 py-3 text-left transition-colors hover:border-acento-ciano/50"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <IconeCantoneira
                      className={`h-4 w-4 text-conteudo-sutil transition-transform ${aberto ? '' : '-rotate-90'}`}
                    />
                    <span className="font-display text-base font-semibold tracking-tight">
                      {g.titulo}
                    </span>
                    <span className="text-xs text-conteudo-sutil">
                      {enviados} de {g.itens.length} enviado{g.itens.length === 1 ? '' : 's'}
                    </span>
                  </span>

                  <span className="min-w-0 text-xs">
                    {travado === undefined ? (
                      <span className="text-economia">Mês em dia</span>
                    ) : (
                      <span className={COR_DA_ACAO[travado.proximaAcao.tom]}>
                        {travado.proximaAcao.responsavelRotulo !== null && (
                          <strong className="font-semibold">
                            {travado.proximaAcao.responsavelRotulo}:{' '}
                          </strong>
                        )}
                        {travado.proximaAcao.resumo}
                      </span>
                    )}
                  </span>
                </button>

                {aberto && (
                  <Tabela
                    legenda={`Cronograma de envios — ${g.titulo}`}
                    densidade="compacta"
                    colunas={
                      leitura
                        ? colunas.filter((c) => c.chave !== 'selecao' && c.chave !== 'acoes')
                        : colunas
                    }
                    dados={g.itens}
                    chaveDaLinha={(l) => l.id}
                    chaveDeCabecalhoDaLinha="emailId"
                    carregando={props.carregando}
                  />
                )}
              </section>
            );
          })}
        </div>
      )}

      {props.ano !== undefined && (
        <ModalDeInsercaoEmLote
          clienteId={props.clienteId}
          ano={props.ano}
          aberto={inserindo}
          aoFechar={() => setInserindo(false)}
        />
      )}

      <ModalDeAprovacao
        clienteId={props.clienteId}
        item={vendoAprovacao}
        aoFechar={() => setVendoAprovacao(null)}
      />

      <ModalDeReprogramacao
        clienteId={props.clienteId}
        item={reprogramando}
        aoFechar={() => setReprogramando(null)}
      />
    </section>
  );
}

/* ─── A leitura do fluxo ──────────────────────────────────────────────────── */

/**
 * Anexar o documento direto da linha, sem abrir o detalhe.
 *
 * O arquivo chega por WhatsApp e o operador quer juntá-lo ao e-mail: o caminho
 * antigo era abrir "Detalhes", achar o seletor de tipo e só então escolher o
 * arquivo. Aqui o clipe abre o seletor de arquivo na hora, já no tipo que a
 * etapa espera; com mais de um documento possível, um menu escolhe qual.
 */
function AnexoRapido(props: {
  readonly linha: LinhaCronograma;
  readonly anexar: UseMutationResult<
    ResultadoDoAnexo,
    Error,
    { emailId: string; chave: string; arquivo: File }
  >;
  readonly aoAnexar: (resultado: ResultadoDoAnexo) => void;
}): JSX.Element {
  const entrada = useRef<HTMLInputElement>(null);
  const escolhida = useRef<string>(DOCUMENTOS_PADRAO[0].chave);

  const candidatos =
    props.linha.documentos.length > 0
      ? props.linha.documentos.map((d) => ({ chave: d.chave, rotulo: d.rotulo }))
      : DOCUMENTOS_PADRAO;

  const abrirSeletor = (chave: string): void => {
    escolhida.current = chave;
    entrada.current?.click();
  };

  const enviando = props.anexar.isPending && props.anexar.variables?.emailId === props.linha.id;

  return (
    <span className="inline-flex items-center">
      <input
        ref={entrada}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp"
        className="hidden"
        onChange={(e) => {
          const arquivo = e.target.files?.[0];
          e.target.value = '';
          if (arquivo === undefined) return;
          props.anexar.mutate(
            { emailId: props.linha.id, chave: escolhida.current, arquivo },
            { onSuccess: (dados) => props.aoAnexar(dados) },
          );
        }}
      />

      {candidatos.length === 1 ? (
        <Botao
          variante="sutil"
          tamanho="pequeno"
          carregando={enviando}
          title={`Anexar ${candidatos[0]?.rotulo.toLowerCase() ?? 'arquivo'}`}
          onClick={() => abrirSeletor(candidatos[0]?.chave ?? DOCUMENTOS_PADRAO[0].chave)}
        >
          <IconeClipe className="h-4 w-4" />
          <span className="sr-only">Anexar arquivo</span>
        </Botao>
      ) : (
        <MenuDeAcoes
          acao="Anexar"
          rotulo={props.linha.titulo}
          icone={<IconeClipe className="h-4 w-4" />}
          itens={candidatos.map((c) => ({
            rotulo: `Anexar ${c.rotulo.toLowerCase()}`,
            aoEscolher: () => abrirSeletor(c.chave),
          }))}
        />
      )}
    </span>
  );
}

/** Os tipos que valem para qualquer item — quando o passo não pede nenhum. */
const DOCUMENTOS_PADRAO = [
  { chave: 'UPLOAD_CERTIFICADO_TRANSF_MES', rotulo: 'Certificado de transferência' },
  { chave: 'UPLOAD_CERTIFICADO_APROPRIACAO_MES', rotulo: 'Certificado de apropriação' },
  { chave: 'UPLOAD_BOLETO_PAGAMENTO', rotulo: 'Boleto de pagamento' },
  { chave: 'UPLOAD_NF_MES', rotulo: 'Nota fiscal' },
] as const;

/**
 * Que documentos a linha mostra.
 *
 * O que a etapa **espera** aparece sempre: é a cobrança. O que o e-mail apenas
 * **leva** só aparece depois que existe — ou quando a etapa já está em jogo.
 * Sem isso, um passo previsto para daqui a dois meses exibia dois "sem arquivo"
 * que não são pendência de ninguém.
 */
function mostrarDocumento(d: DocumentoDaLinha, status: string): boolean {
  return d.papel === 'RESPOSTA' || d.recebido || status !== 'PENDENTE';
}

/** Um documento da etapa: o que já chegou, e o que a operação ainda cobra. */
function ChipDeDocumento(props: { readonly documento: DocumentoDaLinha }): JSX.Element {
  const d = props.documento;
  const titulo =
    d.papel === 'RESPOSTA'
      ? d.recebido
        ? `${d.rotulo} recebido — era o que esta etapa esperava.`
        : `${d.rotulo}: ${d.deRotulo.toLowerCase()} ainda não enviou.`
      : d.recebido
        ? `${d.rotulo} vai anexado neste e-mail.`
        : `${d.rotulo} ainda não existe — o e-mail sai sem ele.`;

  return (
    <span
      title={titulo}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
        d.recebido
          ? 'border-economia/40 bg-economia/10 text-economia'
          : 'border-borda bg-superficie-base text-conteudo-sutil'
      }`}
    >
      <IconeClipe className="h-3 w-3" />
      {d.rotulo}
      <span className="text-[0.65rem] uppercase tracking-wide">
        {d.recebido ? 'recebido' : d.papel === 'RESPOSTA' ? `falta · ${d.deRotulo}` : 'sem arquivo'}
      </span>
    </span>
  );
}

const COR_DA_ACAO: Readonly<Record<LinhaCronograma['proximaAcao']['tom'], string>> = {
  concluido: 'text-conteudo-sutil',
  aguardando: 'text-atencao',
  atencao: 'text-erro',
  neutro: 'text-conteudo-sutil',
};

/* ─── Reprogramar ─────────────────────────────────────────────────────────── */

function ModalDeReprogramacao(props: {
  readonly clienteId: string;
  readonly item: LinhaCronograma | null;
  readonly aoFechar: () => void;
}): JSX.Element {
  const reprogramar = useReprogramar(props.clienteId);
  const [data, setData] = useState('');

  const fechar = (): void => {
    reprogramar.reset();
    setData('');
    props.aoFechar();
  };

  const enviar = (): void => {
    if (props.item === null || data === '') return;
    // O input devolve `AAAA-MM-DD`; a API espera ISO. Meia-noite UTC evita que o
    // fuso do navegador jogue o agendamento para o dia anterior.
    reprogramar.mutate(
      { emailId: props.item.id, dataAgendamento: `${data}T00:00:00.000Z` },
      { onSuccess: fechar },
    );
  };

  return (
    <Modal
      aberto={props.item !== null}
      aoFechar={fechar}
      titulo="Reprogramar envio"
      descricao={props.item === null ? undefined : props.item.titulo}
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao onClick={enviar} disabled={data === ''} carregando={reprogramar.isPending}>
            Reprogramar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-conteudo-primario">Nova data de envio</span>
          <input
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
            className="h-11 rounded-card border border-borda bg-superficie-base px-3 text-sm text-conteudo-primario focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
          />
        </label>

        <Aviso tom="informacao">
          A linha volta para <strong>Agendado</strong> e o worker a considera na nova data. Se a
          data escolhida já passou, ela entra como vencida.
        </Aviso>

        {reprogramar.isError && (
          <ErroDaApi titulo="Não foi possível reprogramar" erro={reprogramar.error} />
        )}
      </div>
    </Modal>
  );
}

/* ─── Auxiliares ──────────────────────────────────────────────────────────── */

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

/** `CLIENTE_APROVA_CREDITO` → `Cliente aprova credito`. */
function humanizar(chave: string): string {
  const t = chave.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function tomDoStatus(status: string): TomSemantico {
  if (status === 'ENVIADO' || status === 'ENVIADO_MANUAL') return 'positivo';
  if (status === 'FALHOU') return 'critico';
  if (status === 'AGENDADO') return 'progresso';
  return 'neutro';
}

function rotuloDoStatus(status: string): string {
  if (status === 'PENDENTE') return 'Aguarda etapa anterior';
  if (status === 'ENVIADO_MANUAL') return 'Enviado (manual)';
  return humanizar(status);
}

/** A posição no fluxo do mês; o que não é etapa vai para o fim da lista. */
function ordemNoFluxo(l: LinhaCronograma): number {
  const ordem = l.passo?.ordem ?? 0;
  return ordem === 0 ? 99 : ordem;
}

/** Ano de operação: o do mês de operação, ou o ano da linha (agendamento). */
function anoDeOperacao(l: LinhaCronograma): number {
  return l.mesOperacao === null
    ? new Date(l.dataAgendamento).getUTCFullYear()
    : Number(l.mesOperacao.slice(0, 4));
}

/** `2027-02` → "Fevereiro de 2027". */
function rotuloDoMesDeOperacao(chave: string): string {
  const [ano, mes] = chave.split('-');
  const nome = MESES[Number(mes) - 1] ?? mes;
  return `${nome} de ${ano}`;
}
